import Style from './useStepAction.module.scss';
import { useCallback, useMemo, useState } from 'react';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { ReactComponent as SkipAllIcon } from 'src/assets/img/common/cross_arrow_double_24.svg';
import { ReactComponent as ApproveAllIcon } from 'src/assets/img/common/cross_double_24.svg';
import { IRouteProgressStepDto, SkipAllFormsOnStepDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { SkipFormState, useFormResultSkipping } from '../formEditor/modals/skipFormResultModal/useFormResultSkipping';
import { ConfirmResult, useConfirmationModal } from '../dialogModal/useConfirmationModal';
import { Trans } from 'react-i18next';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { showServerErrorToast } from '../toast/toast-helper';
import {
  approveAllSkippedFormsOnRouteProgressStep,
  skipAllFormsOnRouteProgressStep,
} from 'src/services/api/api-client/FormsClient';
import { getPatientRouteProgressQueryKey } from 'src/services/api/api-client/StudyRoutesQuery';
import { getPatientQueryKey, getPatientsQueryKey } from 'src/services/api/api-client/PatientQuery';
import {
  getNotificationBatchesQueryKey,
  getNotificationCountsQueryKey,
  getNotificationsQueryKey,
} from 'src/services/api/api-client/NotificationQuery';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { getFormResultsQueryKey } from 'src/services/api/api-client/FormsQuery';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';

export type RightButtonVariant = 'SkipAll' | 'ApproveAll';

export const useStepAction = (
  step: IRouteProgressStepDto,
  patientId: string,
  patientUniqueId: string,
  options?: { withoutTooltips?: boolean },
) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.GeneralInfo.Records');
  const client = useQueryClient();
  const { id: studyId } = useStudy() ?? {};

  const { hasStudyClaim } = useHasStudyClaims();

  const [approving, setApproving] = useState(false);

  const skipAllHandler = useCallback(
    async (formState: SkipFormState) => {
      try {
        const dto = new SkipAllFormsOnStepDto({
          patientId: patientId,
          stepName: step.name,
          skipReason: formState.skipReason!,
          skipReasonDetails: formState.skipReasonDetails,
        });
        await skipAllFormsOnRouteProgressStep(dto);

        await Promise.all([
          client.invalidateQueries(getPatientRouteProgressQueryKey(patientId)),
          client.invalidateQueries(getPatientQueryKey(patientId)),
          client.invalidateQueries(getPatientsQueryKey()),
          client.invalidateQueries(getNotificationCountsQueryKey(studyId)),
          client.invalidateQueries(getNotificationBatchesQueryKey(undefined, [studyId!])),
          client.invalidateQueries(getNotificationsQueryKey(undefined, studyId)),
        ]);
      } catch (e: any) {
        return e.message;
      }
    },
    [client, patientId, step.name, studyId],
  );

  const formResultSkipping = useFormResultSkipping({
    title: t('SkipAllModal.Title'),
    subTitle: step.name,
    text: t('SkipAllModal.Text'),
    callback: skipAllHandler,
  });
  const approveAllConfirmationModal = useConfirmationModal();
  const approveAllHandler = useCallback(async () => {
    const result = await approveAllConfirmationModal.open({
      title: t('ApproveSkippedAllModal.Title'),
      subTitle: step.name,
      text: (
        <span>
          <Trans
            t={t}
            i18nKey={'ApproveSkippedAllModal.Text'}
            values={{ stepName: step.name, uniqId: patientUniqueId }}
            components={{
              Bold: <span className={Style.boldText} />,
              Red: <span className={Style.redText} />,
            }}
          />
        </span>
      ),
      cancelButtonText: t('ApproveSkippedAllModal.CancelButton'),
      okButtonText: t('ApproveSkippedAllModal.ApproveButton'),
    });

    if (result !== ConfirmResult.Confirm) return;

    try {
      setApproving(true);
      await approveAllSkippedFormsOnRouteProgressStep(patientId, step.name);
      await Promise.all([
        client.invalidateQueries(getFormResultsQueryKey(studyId!)),
        client.invalidateQueries(getPatientRouteProgressQueryKey(patientId)),
        client.invalidateQueries(getPatientsQueryKey()),
      ]);
    } catch (error: any) {
      showServerErrorToast(error);
    } finally {
      setApproving(false);
    }
  }, [approveAllConfirmationModal, client, patientId, patientUniqueId, step.name, studyId, t]);

  const rightButton = useMemo(
    () => ({
      SkipAll: {
        icon: SkipAllIcon,
        tooltip: t('SkipAllButton'),
        action: formResultSkipping.start,
        isLoading: false,
      },
      ApproveAll: {
        icon: ApproveAllIcon,
        tooltip: t('ApproveSkippedAllButton'),
        action: approveAllHandler,
        isLoading: approving,
      },
    }),
    [approveAllHandler, approving, formResultSkipping.start, t],
  );

  const renderRightButton = useCallback(() => {
    const variant: RightButtonVariant | undefined =
      step.canSkipAllForms && hasStudyClaim(StudyRoleClaimValue.PatientCreate)
        ? 'SkipAll'
        : step.canApproveAllSkippedForms && hasStudyClaim(StudyRoleClaimValue.ApproveForm)
        ? 'ApproveAll'
        : undefined;

    if (!variant) return undefined;

    return (
      <div className={clsx({ [Style.marginRight]: variant === 'SkipAll' })}>
        <AppButtonWithTooltip
          variant={'icon-link'}
          colorSchema={'primary'}
          Icon={rightButton[variant].icon}
          disabledTooltip={options?.withoutTooltips}
          tooltip={rightButton[variant].tooltip}
          onClick={rightButton[variant].action}
          isLoading={rightButton[variant].isLoading}
        />
      </div>
    );
  }, [hasStudyClaim, options?.withoutTooltips, rightButton, step.canApproveAllSkippedForms, step.canSkipAllForms]);

  return {
    renderRightButton,
    modals: (
      <>
        {approveAllConfirmationModal.modal}
        {formResultSkipping.modal}
      </>
    ),
  };
};
